(function () {
  const DEFAULTS = {
    slides: [],
    gap: 30,
    heightDesktopVh: 85,
    heightMobileVh: 70,
    captionFontSizePx: 10,
    captionFontWeight: 300,
    captionMarginTopPx: 9,
    captionReservePx: 28,
    scrollbarPx: 2,
    autoScrollMobile: false,
    autoScrollIntervalMs: 2000,
    autoScrollResumeMs: 3500,
    mobileBreakpointPx: 768,
    aspectRatio: "9/16" // per-carousel override via config: "2/3", "16/9", etc.
  };

  function parseConfig(root) {
    const attr = root.getAttribute("data-gallery-config");
    if (attr) {
      try {
        return { ...DEFAULTS, ...JSON.parse(attr) };
      } catch (e) {
        console.warn("hs-gallery: invalid JSON in data-gallery-config", e);
      }
    }

    const cfgTag = root.querySelector("[data-gallery-config]");
    if (cfgTag) {
      try {
        return { ...DEFAULTS, ...JSON.parse(cfgTag.textContent || "{}") };
      } catch (e) {
        console.warn("hs-gallery: invalid JSON in script config", e);
      }
    }

    return { ...DEFAULTS };
  }

  function ensureStyles(root, cfg) {
    if (root.querySelector('style[data-hs-gallery-style="1"]')) return;

    const style = document.createElement("style");
    style.setAttribute("data-hs-gallery-style", "1");

    const bp = cfg.mobileBreakpointPx;
    const gap = cfg.gap;
    const hDesk = cfg.heightDesktopVh;
    const hMob = cfg.heightMobileVh;
    const capSize = cfg.captionFontSizePx;
    const capWeight = cfg.captionFontWeight;
    const capMarginTop = cfg.captionMarginTopPx;
    const capReserve = cfg.captionReservePx;
    const sb = cfg.scrollbarPx;

    const ar = (cfg.aspectRatio || DEFAULTS.aspectRatio).replace(/\s+/g, "");
    const parts = ar.split("/");
    const arW = parseFloat(parts[0]) || 9;
    const arH = parseFloat(parts[1]) || 16;

    style.textContent = `
      .hs-gallery {
        position: relative;
        height: ${hDesk}vh;
        width: 100%;
      }

      .hs-gallery__scroller {
        height: 100%;
        width: 100%;
        overflow-x: auto;
        overflow-y: hidden;
        display: flex;
        gap: ${gap}px;
        padding: 0 0 10px 0; /* override on Builder side if needed */
        scroll-snap-type: x mandatory;
        scroll-behavior: smooth;
        -webkit-overflow-scrolling: touch;

        scrollbar-width: thin;
        scrollbar-color: rgba(0,0,0,0.45) transparent;
      }

      .hs-gallery__scroller::-webkit-scrollbar { height: ${sb}px; }
      .hs-gallery__scroller::-webkit-scrollbar-track { background: transparent; }
      .hs-gallery__scroller::-webkit-scrollbar-thumb { background: rgba(0,0,0,0.45); }
      .hs-gallery__scroller::-webkit-scrollbar-thumb:hover { background: rgba(0,0,0,0.65); }

      .hs-gallery__slide {
        height: 100%;
        scroll-snap-align: start;
        flex: 0 0 auto;
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        --caption-space: ${capReserve}px;
        padding-bottom: 2px;
      }

      .hs-gallery__frame {
        aspect-ratio: ${arW} / ${arH};

        height: calc(${hDesk}vh - var(--caption-space));
        width: calc(( ${hDesk}vh - var(--caption-space) ) * ${arW} / ${arH});

        max-height: calc(${hDesk}vh - var(--caption-space));
        max-width: 85vw;

        display: grid;
        place-items: center;
        position: relative;
      }

      .hs-gallery__img {
        width: 100%;
        height: 100%;
        object-fit: contain;
        border-radius: 0;
        display: block;
      }

      .hs-gallery__video {
        width: 100%;
        height: 100%;
        object-fit: contain; /* no cropping */
        border-radius: 0;
        display: block;
      }

      /* Shield to reduce casual saving/opening (images only; we omit for video in JS) */
      .hs-gallery__shield {
        position: absolute;
        inset: 0;
        background: transparent;
        z-index: 2;
      }

      .hs-gallery__caption {
        width: 100%;
        font-size: ${capSize}px;
        font-weight: ${capWeight};
        line-height: 1.2;
        margin-top: ${capMarginTop}px;
        text-align: left;
      }

      /* Caption links: match caption text, underline only, ignore theme link styling */
      .hs-gallery__caption a,
      .hs-gallery__caption a:visited,
      .hs-gallery__caption a:hover,
      .hs-gallery__caption a:active,
      .hs-gallery__caption a:focus {
        color: inherit !important;
        font: inherit !important;
        letter-spacing: inherit !important;
        text-transform: inherit !important;
        text-decoration: underline !important;
        text-decoration-thickness: from-font;
        text-underline-offset: 0.12em;
        background: transparent !important;
        outline: none;
      }

      @media (max-width: ${bp}px) {
        .hs-gallery { height: ${hMob}vh; }

        .hs-gallery__frame {
          height: calc(${hMob}vh - var(--caption-space));
          width: calc(( ${hMob}vh - var(--caption-space) ) * ${arW} / ${arH});

          max-height: calc(${hMob}vh - var(--caption-space));
          max-width: 85vw;
        }
      }
    `;

    root.appendChild(style);
  }

  function buildSlides(scroller, slides) {
    slides.forEach((item, i) => {
      const slide = document.createElement("div");
      slide.className = "hs-gallery__slide";

      const type = (item.type || "image").toLowerCase();
      const captionHTML = item.caption
        ? `<div class="hs-gallery__caption">${item.caption}</div>`
        : ``;

      let mediaHTML = "";

      if (type === "video") {
        const autoplay = item.autoplay !== false; // default true
        const loop = item.loop !== false;         // default true
        const muted = item.muted !== false;       // default true (required for autoplay on iOS)
        const controls = !!item.controls;         // default false
        const posterAttr = item.poster ? ` poster="${item.poster}"` : "";

        mediaHTML = `
          <video
            class="hs-gallery__video"
            src="${item.src}"
            ${posterAttr}
            ${autoplay ? "autoplay" : ""}
            ${loop ? "loop" : ""}
            ${muted ? "muted" : ""}
            playsinline
            ${controls ? "controls" : ""}
            preload="metadata"
          ></video>
        `;
      } else {
        mediaHTML = `
          <img class="hs-gallery__img"
               src="${item.src}"
               alt="${item.alt || "Image " + (i + 1)}"
               loading="lazy">
        `;
      }

      // Keep shield for images (deterrence), omit for videos so they remain usable
      const shieldHTML =
        type === "video"
          ? ``
          : `<div class="hs-gallery__shield" aria-hidden="true"></div>`;

      slide.innerHTML = `
        <div class="hs-gallery__frame">
          ${mediaHTML}
          ${shieldHTML}
        </div>
        ${captionHTML}
      `;

      scroller.appendChild(slide);

      const img = slide.querySelector("img");
      if (img) {
        img.draggable = false;
        img.addEventListener("contextmenu", (e) => e.preventDefault());
      }

      const vid = slide.querySelector("video");
      if (vid && vid.hasAttribute("autoplay")) {
        setTimeout(() => {
          try { vid.play(); } catch (e) {}
        }, 50);
      }
    });
  }

  // Index-based auto-scroll (no early restart)
  function initAutoScroll(scroller, cfg) {
    const mq = window.matchMedia(`(max-width:${cfg.mobileBreakpointPx}px)`);
    let autoTimer = null;
    let resumeTimer = null;
    let index = 0;

    function getSlides() {
      return Array.from(scroller.querySelectorAll(".hs-gallery__slide"));
    }

    function scrollToIndex(i) {
      const slides = getSlides();
      if (!slides.length) return;
      const target = slides[i];
      scroller.scrollTo({ left: target.offsetLeft, behavior: "smooth" });
    }

    function stop() {
      if (autoTimer) clearInterval(autoTimer);
      autoTimer = null;
    }

    function start() {
      stop();
      if (!mq.matches || !cfg.autoScrollMobile) return;

      const slides = getSlides();
      if (!slides.length) return;

      index = Math.min(index, slides.length - 1);

      autoTimer = setInterval(() => {
        const slidesNow = getSlides();
        if (!slidesNow.length) return;

        index = (index + 1) % slidesNow.length;
        scrollToIndex(index);
      }, cfg.autoScrollIntervalMs);
    }

    function pauseThenResume() {
      stop();
      if (resumeTimer) clearTimeout(resumeTimer);
      resumeTimer = setTimeout(start, cfg.autoScrollResumeMs);
    }

    ["pointerdown", "touchstart", "wheel"].forEach((evt) =>
      scroller.addEventListener(evt, pauseThenResume, { passive: true })
    );
    scroller.addEventListener("scroll", pauseThenResume, { passive: true });

    mq.addEventListener("change", () => { index = 0; start(); });
    window.addEventListener("resize", () => { index = 0; start(); });

    start();
  }

  function initGallery(root) {
    const scroller = root.querySelector("[data-scroller]");
    if (!scroller) return;

    if (scroller.dataset.hsgInit === "1") return;
    scroller.dataset.hsgInit = "1";

    const cfg = parseConfig(root);

    root.classList.add("hs-gallery");
    scroller.classList.add("hs-gallery__scroller");

    ensureStyles(root, cfg);
    buildSlides(scroller, cfg.slides);

    if (cfg.autoScrollMobile) initAutoScroll(scroller, cfg);
  }

  function boot() {
    document.querySelectorAll("[data-gallery]").forEach(initGallery);
  }

  boot();
  const obs = new MutationObserver(boot);
  obs.observe(document.documentElement, { childList: true, subtree: true });
})();
